import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Search, Undo2 } from 'lucide-react'
import { listReturns } from '../../api/purchase'
import Spinner from '../../components/Spinner'
import { fmtDay, parseWallClock } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import PurchaseTabs from './PurchaseTabs'
import { useVendorNames } from './purchaseShared'
import { ItemForToggle, useItemFor } from './itemFor'

export default function VendorReturnList() {
  const nav = useNavigate()
  const vendorNames = useVendorNames()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const itemFor = useItemFor()

  useEffect(() => {
    listReturns()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  const vendorOf = (r) => vendorNames[String(r.vendor_id)] || ''
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return rows
      .filter(itemFor.matches)
      .filter((r) => !term || [docNo('return', r.return_no), vendorOf(r), docNo('po', r.po_no), docNo('receipt', r.ref_receipt_no)].join(' ').toLowerCase().includes(term))
      .sort((a, b) => (parseWallClock(b.return_date)?.getTime() || 0) - (parseWallClock(a.return_date)?.getTime() || 0) || num(b.return_id) - num(a.return_id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, vendorNames, itemFor.value])

  return (
    <div className="ci-page">
      <PurchaseTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Returns to vendors</h1>
            <div className="ci-title-meta">Goods you’ve sent back. Each return comes out of stock and off what you owe.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-primary" to={`/purchase/vendor-returns/new${itemFor.query}`}><Undo2 size={15} />New return</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search returns">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Return, vendor or PO…" />
        </label>
        <ItemForToggle itemFor={itemFor} rows={rows} />
      </div>

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                <th>Return</th>
                <th>Date</th>
                <th>Vendor</th>
                <th>Order</th>
                <th>From receipt</th>
                <th className="num">Value</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6"><Spinner label="Loading returns…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">{rows.length ? 'No returns match.' : 'Nothing has been returned to a vendor.'}</div></td></tr>
              ) : shown.map((r) => (
                <tr key={r.return_id} className="row-link" onClick={() => nav(`/purchase/vendor-returns/${r.return_id}`)}>
                  <td><Link className="ci-je-link" to={`/purchase/vendor-returns/${r.return_id}`} onClick={(e) => e.stopPropagation()}>{docNo('return', r.return_no)}</Link></td>
                  <td className="ci-muted-cell">{fmtDay(r.return_date) || '–'}</td>
                  <td className="ci-strong">{vendorOf(r) || '–'}</td>
                  <td>{r.po_id ? <Link className="ci-je-link" to={`/purchase/po-detail/${r.po_id}`} onClick={(e) => e.stopPropagation()}>{docNo('po', r.po_no)}</Link> : '–'}</td>
                  <td>{r.ref_receipt_id ? <Link className="ci-je-link" to={`/purchase/receipts/${r.ref_receipt_id}`} onClick={(e) => e.stopPropagation()}>{docNo('receipt', r.ref_receipt_no)}</Link> : '–'}</td>
                  <td className="num ci-total">{money(r.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} {shown.length === 1 ? 'return' : 'returns'} · {money(shown.reduce((s, r) => s + num(r.total_amount), 0))}</span>
        </div>
      </div>
    </div>
  )
}
